import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';

const BandInlineForm = ({ band, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    code: '',
    title: '',
    age_min_months: 0,
    age_max_months: 0,
    is_active: true,
  });
  const [milestoneImage, setMilestoneImage] = useState(null);
  const [goalImage, setGoalImage] = useState(null);
  const [milestoneImagePreview, setMilestoneImagePreview] = useState(null);
  const [goalImagePreview, setGoalImagePreview] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (band) {
      setFormData({
        code: band.code,
        title: band.title,
        age_min_months: band.age_min_months,
        age_max_months: band.age_max_months,
        is_active: band.is_active,
      });
      setMilestoneImagePreview(band.milestone_image);
      setGoalImagePreview(band.goal_image);
    }
  }, [band]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      if (name === 'milestone_image') {
        setMilestoneImage(files[0]);
      } else if (name === 'goal_image') {
        setGoalImage(files[0]);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.code.trim()) newErrors.code = 'Code is required';
    if (formData.age_min_months < 0) newErrors.age_min_months = 'Min age must be positive';
    if (formData.age_max_months < 0) newErrors.age_max_months = 'Max age must be positive';
    if (formData.age_max_months <= formData.age_min_months) {
      newErrors.age_max_months = 'Max age must be greater than min age';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = validate();
    if (Object.keys(newErrors).length === 0) {
      onSave({ ...formData, milestone_image: milestoneImage, goal_image: goalImage });
    } else {
      setErrors(newErrors);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="title"
            placeholder="e.g., Infancy"
            value={formData.title}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]`}
          />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Code <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="code"
            placeholder="e.g., INF"
            value={formData.code}
            onChange={handleChange}
            className={`w-full px-3 py-2 border ${errors.code ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]`}
          />
          {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Min Age (Months) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="age_min_months"
            placeholder="0"
            value={formData.age_min_months}
            onChange={handleChange}
            min="0"
            className={`w-full px-3 py-2 border ${errors.age_min_months ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]`}
          />
          {errors.age_min_months && <p className="text-red-500 text-xs mt-1">{errors.age_min_months}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Max Age (Months) <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="age_max_months"
            placeholder="12"
            value={formData.age_max_months}
            onChange={handleChange}
            min="0"
            className={`w-full px-3 py-2 border ${errors.age_max_months ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]`}
          />
          {errors.age_max_months && <p className="text-red-500 text-xs mt-1">{errors.age_max_months}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Milestone Image
          </label>
          <input
            type="file"
            name="milestone_image"
            onChange={handleChange}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
          />
          {milestoneImagePreview && !milestoneImage && (
            <div className="mt-2">
              <img src={milestoneImagePreview} alt="Milestone" className="w-20 h-20 object-cover rounded-lg" />
              <p className="text-xs text-gray-500 mt-1">Current Image</p>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Goal Image
          </label>
          <input
            type="file"
            name="goal_image"
            onChange={handleChange}
            className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
          />
          {goalImagePreview && !goalImage && (
            <div className="mt-2">
              <img src={goalImagePreview} alt="Goal" className="w-20 h-20 object-cover rounded-lg" />
              <p className="text-xs text-gray-500 mt-1">Current Image</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          className="w-4 h-4 text-[#1e3a5f] border-gray-300 rounded focus:ring-2 focus:ring-[#1e3a5f]"
          id="is_active_inline"
        />
        <label htmlFor="is_active_inline" className="text-sm font-medium text-gray-700 cursor-pointer">
          Active Band
        </label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold hover:bg-gray-50 transition-colors flex items-center gap-1"
        >
          <X size={16} /> Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-gradient-to-r from-[#1e3a5f] to-[#2a4b73] text-white rounded-lg font-semibold hover:shadow-lg transition-shadow flex items-center gap-1"
        >
          <Save size={16} /> {band ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
};

export default BandInlineForm;
